/**
 * Cross-browser per-user state for the "What's New since your last visit"
 * briefing, held in a JetStream stream.
 *
 * ## Why not a KV bucket
 *
 * A KV bucket was the obvious fit, but `views.kv().put()` publishes to
 * `$KV.<bucket>.<key>`, and this deployment's NATS auth callout does not grant
 * the frontend permission on `$KV.>` — it fails with
 * "Permissions Violation for Publish to $KV.USER_INSIGHTS.<key>". The `user.>`
 * namespace *is* granted, which is how navigation history already writes.
 *
 * So this reproduces KV semantics inside the permitted namespace: one subject per
 * user, and `max_msgs_per_subject: 1` on the stream so only the newest value is
 * retained.
 *
 * ## Why `getMessage` rather than a consumer
 *
 * `NavigationHistoryService` reads with a *durable* consumer that acks what it
 * reads. That only works once: the durable position advances past the messages,
 * so later calls — including from another browser — return nothing. It also
 * races a 3 s timeout and usually loses. `getMessage({ last_by_subj })` is a
 * single request with no durable state and no acks, so it returns the current
 * value every time.
 *
 * ## Why `generatedAt` doubles as the visit marker
 *
 * Storing a separate per-page-load "last seen" timestamp would be wrong: a
 * refresh five minutes later would overwrite it and collapse the reporting window
 * to nothing. Pinning the window to the last *generation* is what lets a
 * two-week-old visit still produce a two-week summary.
 */

import { natsClient } from "./NATSClient";

/**
 * Bump when the stored shape or its expected contents change; older entries are
 * then ignored rather than crashed on, which forces one clean regeneration.
 *
 * 2 — entries written before the agent's tool-loop narration was trimmed out, and
 *     before the dashboard-oriented instructions, hold prose that should never
 *     render. Invalidating them is what stops a stale briefing persisting.
 */
export const USER_INSIGHT_SCHEMA_VERSION = 2;

const STREAM_NAME = "USER_INSIGHTS";
const SUBJECT_PREFIX = "user.insights";
const LOCAL_FALLBACK_PREFIX = "user_insight_state_";

/** Entries expire after this long, so dormant accounts don't pin storage. */
const MAX_AGE_NANOS = 180 * 24 * 60 * 60 * 1_000_000_000;

/**
 * Hard ceiling on the model-authored HTML we persist. Guards both the NATS
 * message size limit and the render cost of a runaway response.
 */
export const MAX_SUMMARY_HTML_CHARS = 200_000;

/** A machine the briefing refers to, rendered as a real link outside the iframe. */
export interface InsightMachineRef {
  /** Serial number / business key, used to build the deep link. */
  id: string;
  label?: string;
}

export interface StoredInsightSummary {
  /**
   * Model-authored markup for the dashboard. Only ever rendered inside a
   * sandboxed iframe — never injected into the app's DOM.
   */
  html: string;
  /** Machines referenced, so navigation can live outside the sandbox. */
  machines: InsightMachineRef[];
  /** The free-text Keycloak role used, or null when the briefing is generic. */
  role: string | null;
}

export interface UserInsightState {
  version: number;
  /** ISO timestamp of when this summary was generated. */
  generatedAt: string;
  /** ISO timestamp for the start of the period the summary covers. */
  windowStart: string;
  summary: StoredInsightSummary;
}

/**
 * Reduce a user key to a single NATS subject token.
 *
 * The stream binds `user.insights.*`, which matches exactly one token, so dots
 * in a username would place the message on an uncaptured subject and it would be
 * silently dropped.
 */
function toSubjectToken(userKey: string): string {
  return userKey.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 128);
}

function subjectFor(userKey: string): string {
  return `${SUBJECT_PREFIX}.${toSubjectToken(userKey)}`;
}

function isUserInsightState(value: unknown): value is UserInsightState {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<UserInsightState>;
  return (
    v.version === USER_INSIGHT_SCHEMA_VERSION &&
    typeof v.generatedAt === "string" &&
    typeof v.windowStart === "string" &&
    !!v.summary &&
    typeof v.summary.html === "string" &&
    Array.isArray(v.summary.machines) &&
    isUsableBody(v.summary.html)
  );
}

/**
 * Reject a stored body that isn't a real briefing.
 *
 * The agent answers a blocked request with a plain sentence over a *successful*
 * stream, so a refusal can reach storage. Treating such an entry as invalid means
 * the cache heals itself on the next load instead of serving the refusal for the
 * rest of the day.
 */
function isUsableBody(html: string): boolean {
  const lower = html.toLowerCase();
  if (
    lower.includes("blocked by security filters") ||
    lower.includes("blocked by content filters")
  ) {
    return false;
  }
  return /<[a-zA-Z][^>]*>/.test(html);
}

/**
 * Create the stream on first use, mirroring how `NavigationHistoryService`
 * provisions `USER_NAVIGATION`.
 *
 * `max_msgs_per_subject: 1` is what makes this behave like a KV bucket — each
 * write supersedes the previous one for that user.
 */
async function ensureStream(): Promise<boolean> {
  if (!natsClient.isConnected()) return false;
  try {
    const jsm = natsClient.jetstreamManager();
    await jsm.streams.info(STREAM_NAME).catch(async () => {
      await jsm.streams.add({
        name: STREAM_NAME,
        subjects: [`${SUBJECT_PREFIX}.*`],
        retention: "limits" as any,
        max_msgs_per_subject: 1,
        max_age: MAX_AGE_NANOS,
        storage: "file" as any,
      });
      console.log(`Created JetStream stream: ${STREAM_NAME}`);
    });
    return true;
  } catch (error) {
    console.warn("UserInsightStore: stream unavailable:", error);
    return false;
  }
}

// ─── localStorage fallback ───────────────────────────────────────────────────

function readLocal(userKey: string): UserInsightState | null {
  try {
    const raw = localStorage.getItem(`${LOCAL_FALLBACK_PREFIX}${userKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isUserInsightState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocal(userKey: string, state: UserInsightState): void {
  try {
    localStorage.setItem(
      `${LOCAL_FALLBACK_PREFIX}${userKey}`,
      JSON.stringify(state),
    );
  } catch {
    /* quota exceeded or storage blocked — the briefing just regenerates */
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Read the user's briefing state, newest value only.
 *
 * Returns `null` on a genuine first visit, on a stale schema, or when NATS is
 * unreachable — in which case localStorage is consulted so an outage degrades to
 * per-browser behaviour rather than failing.
 */
export async function readUserInsightState(
  userKey: string,
): Promise<UserInsightState | null> {
  if (!(await ensureStream())) return readLocal(userKey);

  try {
    const jsm = natsClient.jetstreamManager();
    const msg = await jsm.streams.getMessage(STREAM_NAME, {
      last_by_subj: subjectFor(userKey),
    });
    const parsed = JSON.parse(new TextDecoder().decode(msg.data)) as unknown;
    return isUserInsightState(parsed) ? parsed : null;
  } catch {
    // No message for this subject yet is the normal first-visit path and is
    // indistinguishable from an error here, so fall back quietly.
    return readLocal(userKey);
  }
}

/**
 * Persist the user's briefing state. Writes localStorage first (synchronous, so
 * it survives even if NATS rejects), then publishes.
 *
 * A plain core publish is used deliberately: it is the exact call that
 * `NavigationHistoryService` already makes successfully under this account's
 * permissions. The stream captures it because it binds `user.insights.*`.
 *
 * Oversized HTML is truncated rather than rejected — a clipped briefing beats a
 * failed write, which would make the app regenerate on every load.
 */
export async function writeUserInsightState(
  userKey: string,
  state: UserInsightState,
): Promise<void> {
  const safe: UserInsightState = {
    ...state,
    summary: {
      ...state.summary,
      html: state.summary.html.slice(0, MAX_SUMMARY_HTML_CHARS),
    },
  };

  writeLocal(userKey, safe);

  if (!(await ensureStream())) return;

  try {
    await natsClient.publish(subjectFor(userKey), JSON.stringify(safe));
  } catch (error) {
    console.warn("UserInsightStore: publish failed:", error);
  }
}

/** True when the two instants fall on the same local calendar day. */
export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Whether a fresh briefing is due.
 *
 * Measured on the local calendar day rather than a rolling 24 h window, so the
 * briefing lands at the start of a working day instead of drifting later each
 * morning.
 */
export function isSummaryDue(
  state: UserInsightState | null,
  now: Date = new Date(),
): boolean {
  if (!state) return true;
  const generated = new Date(state.generatedAt);
  if (Number.isNaN(generated.getTime())) return true;
  return !isSameLocalDay(generated, now);
}
